import { type FormEvent, useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useQueryClient } from "@tanstack/react-query"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { useTeamInvites } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"
import { useOnboarding } from "@/lib/onboarding/onboarding-context"
import { ROLES_BY_TEAM_TYPE } from "@/lib/team/roles"
import {
  Field,
  PrimaryButton,
  StaggerField,
  StepActions,
  StepLayout,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "./onboarding-shell"
import { OnboardingContextPanel } from "./context-panel"
import { ArrowRight, CheckCircle, Mail01, Plus, Trash01, UserPlus01 } from "@untitledui/icons"
import { toast } from "sonner"
import { cx } from "@/lib/utils/cx"

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())

export function InviteMembersStep() {
  const { selectedType, createdTeamId, goBack, goNext, setInvitedCount } = useOnboarding()
  const allowedRoles =
    selectedType ? (ROLES_BY_TEAM_TYPE[selectedType] as readonly string[]) : []
  const queryClient = useQueryClient()
  const invitesQuery = useTeamInvites(createdTeamId)
  const sent = (invitesQuery.data ?? []).map((inv) => ({
    uuid: inv.uuid,
    email: inv.user.email,
    role: inv.role,
  }))

  // Mirror sent count into context so the right-column panel reflects it on
  // step nav (and the "Team" status row lights up as soon as one invite lands).
  useEffect(() => {
    setInvitedCount(sent.length)
  }, [sent.length, setInvitedCount])

  const [email, setEmail] = useState("")
  const [role, setRole] = useState<string>(allowedRoles[0] ?? "")
  const [emailError, setEmailError] = useState<string | undefined>()
  const [sending, setSending] = useState(false)
  const [revokingUuid, setRevokingUuid] = useState<string | null>(null)

  const formHasContent = email.trim().length > 0
  const formIsValid = isEmail(email) && !!role

  const sendInvite = async (): Promise<boolean> => {
    if (!createdTeamId) return false
    if (!formIsValid) {
      setEmailError("Enter a valid email")
      return false
    }
    setSending(true)
    try {
      await teamsApi.invite(createdTeamId, { email: email.trim(), role })
      await queryClient.invalidateQueries({ queryKey: queryKeys.invites.byTeam(createdTeamId) })
      setEmail("")
      setEmailError(undefined)
      return true
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't send the invite"
      toast.error(msg)
      return false
    } finally {
      setSending(false)
    }
  }

  const revokeInvite = async (uuid: string) => {
    if (!createdTeamId || revokingUuid) return
    setRevokingUuid(uuid)
    try {
      await teamsApi.revokeInvite(createdTeamId, uuid)
      await queryClient.invalidateQueries({ queryKey: queryKeys.invites.byTeam(createdTeamId) })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't remove the invite"
      toast.error(msg)
    } finally {
      setRevokingUuid(null)
    }
  }

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault()
    await sendInvite()
  }

  const handleContinue = async () => {
    // If the user filled the form but never hit "Add", send the invite before
    // advancing — otherwise the input is silently lost.
    if (formHasContent) {
      if (!formIsValid) {
        setEmailError("Enter a valid email or skip for now")
        return
      }
      const ok = await sendInvite()
      if (!ok) return
    }
    goNext()
  }

  const isGroup = selectedType === "DealershipGroup"

  return (
    <StepLayout
      eyebrow={isGroup ? "Step 4 — Group leadership" : "Step 4 — Team"}
      title={isGroup ? "Invite your group leadership" : "Invite the people you work with"}
      subtitle={
        isGroup
          ? "Add the Directors and General Managers who oversee your group. You'll add per-location GMs in the next step."
          : "They'll see estimates, updates, and conversations the moment they accept. You can always add more later."
      }
      hero={<OnboardingContextPanel />}
    >
      <div className="space-y-5">
        <AnimatePresence initial={false}>
          {sent.length > 0 && (
            <motion.div
              key="sent-list"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <ul className="flex flex-col gap-1.5">
                <AnimatePresence initial={false}>
                  {sent.map((s) => {
                    const isRevoking = revokingUuid === s.uuid
                    return (
                      <motion.li
                        key={s.uuid}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -8, transition: { duration: 0.2 } }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="flex items-center justify-between rounded-xl border border-emerald-200/60 bg-emerald-50/40 px-3.5 py-2.5"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <CheckCircle className="size-4 shrink-0 text-emerald-600" aria-hidden />
                          <span className="truncate text-[14px] font-medium text-zinc-900">
                            {s.email}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                            {s.role}
                          </span>
                          <button
                            type="button"
                            onClick={() => void revokeInvite(s.uuid)}
                            disabled={revokingUuid !== null}
                            aria-label={`Remove invite for ${s.email}`}
                            className={cx(
                              "inline-flex size-7 items-center justify-center rounded-lg text-zinc-400 transition-all duration-150",
                              "hover:bg-red-50 hover:text-red-600",
                              "focus:outline-none focus:ring-2 focus:ring-red-100",
                              "disabled:cursor-not-allowed disabled:opacity-40",
                            )}
                          >
                            {isRevoking ? (
                              <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-700" />
                            ) : (
                              <Trash01 className="size-3.5" aria-hidden />
                            )}
                          </button>
                        </div>
                      </motion.li>
                    )
                  })}
                </AnimatePresence>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        <form
          onSubmit={handleAdd}
          className="rounded-2xl border border-zinc-200/80 bg-zinc-50/60 p-4 space-y-4"
          noValidate
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            {sent.length > 0 ? "Add another invite" : "Send an invite"}
          </p>
          <StaggerField index={0}>
            <Field label="Email" required error={emailError}>
              <div className="relative">
                <Mail01
                  className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
                  aria-hidden
                />
                <input
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError(undefined)
                  }}
                  inputMode="email"
                  placeholder="teammate@company.com"
                  autoComplete="email"
                  className={cx(
                    onboardingInputClass,
                    "pl-10",
                    emailError && onboardingInputErrorClass,
                  )}
                />
              </div>
            </Field>
          </StaggerField>

          <StaggerField index={1}>
            <Field label="Role" required>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={cx(onboardingInputClass, "appearance-none cursor-pointer pr-8")}
              >
                {allowedRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
          </StaggerField>

          <div className="flex items-center justify-end pt-1">
            <motion.button
              type="submit"
              disabled={sending || !formHasContent}
              initial={false}
              animate={{ scale: formHasContent ? 1 : 0.98 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className={cx(
                "inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-[14px] font-semibold transition-all duration-200",
                "disabled:cursor-not-allowed",
                formHasContent
                  ? "bg-emerald-600 text-white shadow-sm shadow-emerald-600/25 hover:bg-emerald-700 active:scale-[0.97]"
                  : "bg-transparent text-emerald-700/70 hover:bg-emerald-50/80",
              )}
            >
              {sending ? (
                <span className="inline-block size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                <>
                  <Plus className="size-4" aria-hidden />
                  Add invite
                </>
              )}
            </motion.button>
          </div>
        </form>

        <StaggerField index={2}>
          <div className="rounded-xl bg-zinc-50/70 border border-zinc-200/70 px-4 py-3 flex items-start gap-3">
            <UserPlus01 className="size-4 text-emerald-500 mt-0.5 shrink-0" aria-hidden />
            <p className="text-[12.5px] text-zinc-600 leading-relaxed">
              Each person gets an email invite. They can accept anytime — you can revoke,
              re-invite, or change roles from{" "}
              <span className="font-semibold">Settings → Team</span>.
            </p>
          </div>
        </StaggerField>

        <StaggerField index={3}>
          <StepActions
            onBack={goBack}
            skip={
              sent.length === 0
                ? { onSkip: goNext, disabled: sending, label: "I'll do this later" }
                : undefined
            }
            primary={
              <PrimaryButton
                type="button"
                onClick={handleContinue}
                loading={sending}
                variant="emerald"
              >
                {sent.length > 0 ? "Save & continue" : "Continue"}
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </div>
    </StepLayout>
  )
}
